// ---------------------------------------------------------------------------
// Settings — Tenant Configuration & Case Settings
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, Minus } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useToast } from '@/components/ui/toast';
import { useOrgNodes, type OrgNode } from '@/hooks/use-org-nodes';
import { useTenantConfig, useUpdateTenantConfig, useCaseConfig, useUpdateCaseSettings } from '@/hooks/use-config';
import { EffectiveConfigView } from '@/components/settings/EffectiveConfigView';
import { TenantConfigForm } from '@/components/settings/TenantConfigForm';
import { CaseSettingsForm } from '@/components/settings/CaseSettingsForm';
import type { EffectiveCaseConfig, CaseSurveySettings } from '@/types/config';

// ---------------------------------------------------------------------------
// Tenant Configuration tab
// ---------------------------------------------------------------------------

// PLM-726: the customer is picked here. It used to be the token's
// custom:orgNodeId, the user's lowest-numbered assigned org node, which was a
// customer only by coincidence and is no longer issued. The list is the
// customers the user can read (shared-services POST /org/nodes, RLS-scoped),
// which is the same check POST /config/tenant applies, so every entry loads.
function TenantConfigTab() {
  const { data: customers, isLoading, error } = useOrgNodes();
  const [pickedId, setPickedId] = useState<string | null>(null);
  // With exactly one customer there is nothing to choose.
  const customerId = pickedId ?? (customers?.length === 1 ? customers[0].id : null);
  const customer = customers?.find((c) => c.id === customerId) ?? null;

  return (
    <div className="space-y-4">
      <CustomerPicker
        customers={customers}
        isLoading={isLoading}
        failed={!!error}
        value={customerId}
        onChange={setPickedId}
      />
      {customer ? (
        // Keyed so switching customer drops any half-finished edit.
        <TenantConfigPanel key={customer.id} customer={customer} />
      ) : (
        !isLoading &&
        !error &&
        (customers?.length ?? 0) > 0 && (
          <p className="text-sm text-carbon-500">
            Select a customer to view its survey configuration.
          </p>
        )
      )}
    </div>
  );
}

function CustomerPicker({
  customers,
  isLoading,
  failed,
  value,
  onChange,
}: {
  customers: OrgNode[] | undefined;
  isLoading: boolean;
  failed: boolean;
  value: string | null;
  onChange: (id: string) => void;
}) {
  return (
    <div className="max-w-sm">
      <Label htmlFor="settingsCustomer" className="mb-1">Customer</Label>
      {isLoading ? (
        <Input id="settingsCustomer" disabled placeholder="Loading customers..." />
      ) : failed ? (
        <p className="text-sm text-red-600">Failed to load customers.</p>
      ) : (customers?.length ?? 0) === 0 ? (
        <p className="text-sm text-carbon-500">No customers are available to you.</p>
      ) : (
        <Select value={value ?? ''} onValueChange={onChange}>
          <SelectTrigger id="settingsCustomer">
            <SelectValue placeholder="Select customer" />
          </SelectTrigger>
          <SelectContent>
            {customers!.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}

function TenantConfigPanel({ customer }: { customer: OrgNode }) {
  const { user } = useAuth();
  const tenantId = Number(customer.id);
  const isAdmin = user!.role === 'TCM Admin';

  const { data, isLoading, error } = useTenantConfig(tenantId);
  const updateMutation = useUpdateTenantConfig(tenantId);
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-red-600">
          Failed to load tenant configuration.
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  if (editing && isAdmin) {
    return (
      <TenantConfigForm
        config={data.effective}
        isPending={updateMutation.isPending}
        onCancel={() => setEditing(false)}
        onSubmit={(values) => {
          updateMutation.mutate(values, {
            onSuccess: () => {
              toast({ title: 'Configuration saved', variant: 'success' });
              setEditing(false);
            },
            onError: () => {
              toast({ title: 'Failed to save configuration', variant: 'destructive' });
            },
          });
        }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-carbon-900">Effective Configuration</h3>
          <Badge variant="secondary">{customer.name}</Badge>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>
      <EffectiveConfigView config={data.effective} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Case Settings tab
// ---------------------------------------------------------------------------

function CaseSettingsTab() {
  const [caseIdInput, setCaseIdInput] = useState('');
  const [loadedCaseId, setLoadedCaseId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);

  const { data, isLoading, error } = useCaseConfig(loadedCaseId);
  const updateMutation = useUpdateCaseSettings(loadedCaseId ?? 0);
  const { toast } = useToast();

  const handleLoad = () => {
    const parsed = parseInt(caseIdInput, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      setLoadedCaseId(parsed);
      setEditing(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Case ID lookup */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Look Up Case</CardTitle>
          <CardDescription>Enter a case ID to view and edit its survey settings.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3 max-w-sm">
            <div className="flex-1">
              <Label htmlFor="caseId" className="mb-1">Case ID</Label>
              <Input
                id="caseId"
                type="number"
                min={1}
                placeholder="e.g. 12345"
                value={caseIdInput}
                onChange={(e) => setCaseIdInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleLoad(); }}
              />
            </div>
            <Button onClick={handleLoad} disabled={!caseIdInput}>Load</Button>
          </div>
        </CardContent>
      </Card>

      {/* Loading / error states */}
      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {error && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-red-600">
            Failed to load case settings. Verify the case ID exists.
          </CardContent>
        </Card>
      )}

      {/* Loaded case data */}
      {data && !editing && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-carbon-900">Case {data.caseId} Settings</h3>
              <Badge variant="secondary">Effective (cascaded)</Badge>
            </div>
            <Button size="sm" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </div>
          <CaseEffectiveView effective={data.effective} settings={data.settings} />
        </div>
      )}

      {data && editing && (
        <CaseSettingsForm
          effective={data.effective}
          settings={data.settings}
          isPending={updateMutation.isPending}
          onCancel={() => setEditing(false)}
          onSubmit={(values) => {
            updateMutation.mutate(values, {
              onSuccess: () => {
                toast({ title: 'Case settings saved', variant: 'success' });
                setEditing(false);
              },
              onError: () => {
                toast({ title: 'Failed to save case settings', variant: 'destructive' });
              },
            });
          }}
        />
      )}
    </div>
  );
}

// -- Read-only case effective view --

const DELIVERY_LABELS: Record<string, string> = {
  voice: 'Voice',
  sms: 'SMS',
  email: 'Email',
  web: 'Web',
  mobile: 'Mobile',
};

function BoolIndicator({ value }: { value: boolean }) {
  return value ? (
    <Check className="inline h-4 w-4 text-green-600" />
  ) : (
    <Minus className="inline h-4 w-4 text-silver-400" />
  );
}

function SourceBadge({ isCustom }: { isCustom: boolean }) {
  return isCustom ? (
    <Badge variant="default" className="ml-2 text-[10px]">Custom</Badge>
  ) : (
    <Badge variant="secondary" className="ml-2 text-[10px]">Inherited</Badge>
  );
}

function CaseEffectiveView({
  effective,
  settings,
}: {
  effective: EffectiveCaseConfig;
  settings: CaseSurveySettings | null;
}) {
  const has = (field: keyof CaseSurveySettings) => settings != null && settings[field] != null;

  return (
    <Card>
      <CardContent className="pt-5">
        <div className="space-y-3">
          <Row label="Max Calls per Day" badge={<SourceBadge isCustom={has('max_calls_per_day')} />}>
            {effective.maxCallsPerDay}
          </Row>
          <Row label="Ignore Alert Triggered" badge={<SourceBadge isCustom={has('ignore_alert_triggered')} />}>
            <BoolIndicator value={effective.ignoreAlertTriggered} />
          </Row>
          <Row label="Ignore Noncompliance" badge={<SourceBadge isCustom={has('ignore_noncompliance')} />}>
            <BoolIndicator value={effective.ignoreNoncompliance} />
          </Row>
          <Row label="Ignore Weekend Alert Triggered" badge={<SourceBadge isCustom={has('ignore_weekend_alert_triggered')} />}>
            <BoolIndicator value={effective.ignoreWeekendAlertTriggered} />
          </Row>
          <Row label="Delivery Preference" badge={<SourceBadge isCustom={has('delivery_type_preference')} />}>
            {DELIVERY_LABELS[effective.deliveryTypePreference] ?? effective.deliveryTypePreference}
          </Row>
          <Row label="IVR Enabled">
            <BoolIndicator value={effective.ivrEnabled} />
          </Row>
          <Row label="No Weekend Calls">
            <BoolIndicator value={effective.noWeekendCalls} />
          </Row>
        </div>
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  badge,
  children,
}: {
  label: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-silver-100 last:border-0">
      <span className="text-sm text-carbon-600">
        {label}
        {badge}
      </span>
      <span className="text-sm font-medium text-carbon-900">{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Settings page
// ---------------------------------------------------------------------------

export default function Settings() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-carbon-900">Settings</h2>
        <p className="text-sm text-carbon-500">Tenant configuration and case-level survey preferences</p>
      </div>

      <Tabs defaultValue="tenant">
        <TabsList>
          <TabsTrigger value="tenant">Tenant Configuration</TabsTrigger>
          <TabsTrigger value="case">Case Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="tenant">
          <TenantConfigTab />
        </TabsContent>

        <TabsContent value="case">
          <CaseSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
